import { useState, useEffect, useRef } from 'react';
import { IconButton, Badge, keyframes } from '@mui/material';
import {
  CloudOff,
  CloudSync,
  CloudDone,
  CloudQueue
} from '@mui/icons-material';
import useSyncStatus from '@hooks/useSyncStatus';
import useOnlineStatus from '@hooks/useOnlineStatus';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

type VisualState = 'hidden' | 'pending' | 'syncing' | 'synced' | 'failed';

interface SyncStatusIndicatorProps {
  onTap?: () => void;
}

const SyncStatusIndicator = ({ onTap }: SyncStatusIndicatorProps) => {
  const { pending, failed, syncing } = useSyncStatus();
  const isOnline = useOnlineStatus();
  const [showSynced, setShowSynced] = useState(false);
  const prevTotalRef = useRef(0);

  const total = pending + failed + syncing;

  // Show brief "synced" state when all items finish
  useEffect(() => {
    if (prevTotalRef.current > 0 && total === 0 && failed === 0) {
      setShowSynced(true);
      const timer = setTimeout(() => setShowSynced(false), 3000);
      return () => clearTimeout(timer);
    }
    prevTotalRef.current = total;
  }, [total, failed]);

  const state: VisualState = (() => {
    if (showSynced) return 'synced';
    if (failed > 0) return 'failed';
    if (syncing > 0) return 'syncing';
    if (pending > 0) return 'pending';
    return 'hidden';
  })();

  if (state === 'hidden') return null;

  const icon = (() => {
    switch (state) {
      case 'syncing':
        return (
          <CloudSync
            sx={{ animation: `${spin} 1.5s linear infinite` }}
            fontSize="small"
          />
        );
      case 'synced':
        return <CloudDone fontSize="small" />;
      case 'failed':
        return <CloudOff fontSize="small" />;
      case 'pending':
        return isOnline ? (
          <CloudQueue fontSize="small" />
        ) : (
          <CloudOff fontSize="small" />
        );
    }
  })();

  const badgeCount = state === 'failed' ? failed : pending + syncing;
  const badgeColor = state === 'failed' ? 'error' : 'info';

  return (
    <IconButton
      size="small"
      onClick={onTap}
      sx={{
        color:
          state === 'failed'
            ? 'error.main'
            : state === 'synced'
              ? 'success.main'
              : 'text.secondary',
        transition: 'color 0.2s ease-in-out'
      }}
    >
      {badgeCount > 0 ? (
        <Badge
          badgeContent={badgeCount}
          color={badgeColor}
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              height: 16,
              minWidth: 16
            }
          }}
        >
          {icon}
        </Badge>
      ) : (
        icon
      )}
    </IconButton>
  );
};

export default SyncStatusIndicator;
